import { _js, ___p } from '@ifixit/utils/localize';
import { Badge } from '@ifixit/ui/components/badge';
import { FaIcon } from '@ifixit/ui/components/icon';
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from '@ifixit/ui/components/select';
import { Skeleton } from '@ifixit/ui/components/skeleton';
import { cn } from '@ifixit/ui/lib/utils';
import {
   type KeyboardEvent as ReactKeyboardEvent,
   useEffect,
   useMemo,
   useRef,
   useState,
} from 'react';
import { localizeAndFormat } from 'Shared/utils/numberFormat';
import { COLUMN_LABEL_ON_FILL_CLASS } from 'Shared/editor/field';
import { DashboardPagination } from 'Shared/editor/dashboard_pagination';
import { usePageParam, useUrlParam } from 'Shared/editor/url_params';
import { FooterCard } from 'Shared/editor/footer_card';
import { MessageCard } from 'Shared/editor/message_card';
import { useHashTab } from 'Shared/editor/hash_tabs';
import { LanguagePairChips } from 'Shared/editor/language_pair_chips';
import type {
   PatrolLanguageFilter,
   PatrolQueueKey,
   PatrolQueues,
   PatrolProps,
   PatrolRow,
   PatrolViewKey,
} from './patrol_types';
import { usePatrolQueues } from './use_patrol_api';

// Shell, card, and list grammar mirror the /Translate quality dashboard — a sibling
// tab in the community nav — so the two pages read as one surface.

const QUEUE_CARD_CLASS =
   'overflow-hidden rounded-2xl border border-gray-300 bg-white max-md:-mx-4 max-md:rounded-none max-md:border-x-0';

// Shared by the rows and their header: thumbnail, document, right-aligned revision
// count. Who edited it and when reads under the title rather than as its own column,
// since the queues aren't all sorted by that date (wikis and areas sort by oldest
// pending edit instead).
const QUEUE_GRID_CLASS =
   'md:grid md:grid-cols-[3.5rem_minmax(0,1fr)_5.5rem] md:items-center md:gap-x-3';

const ITEMS_PER_PAGE = 25;

const VIEW_CARDS: Array<{
   key: PatrolViewKey;
   icon: string;
   title: () => string;
   description: () => string;
}> = [
   // First, and where the page opens: most of a patroller's work is
   // whatever is oldest, whichever queue it happens to sit in.
   {
      key: 'all',
      icon: 'layer-group',
      title: () => _js('All'),
      description: () => _js('Everything waiting for review, oldest first.'),
   },
   {
      key: 'guides',
      icon: 'screwdriver-wrench',
      title: () => _js('Guides'),
      description: () => _js('Step-by-step repair guides with edits awaiting review.'),
   },
   {
      key: 'wikis',
      icon: 'file-lines',
      title: () => _js('Wikis'),
      description: () => _js('Device and topic pages with edits awaiting review.'),
   },
   {
      key: 'areas',
      icon: 'sitemap',
      title: () => _js('Wiki Areas'),
      description: () => _js('Pages moved to a different place in the device hierarchy.'),
   },
];

const VIEW_KEYS = VIEW_CARDS.map(card => card.key);

const QUEUE_KEYS: PatrolQueueKey[] = ['guides', 'wikis', 'areas'];

// In the merged view the tab no longer says what a row is, so the row does.
// Singular: it names the one document, not the queue it came from.
const QUEUE_LABELS: Record<PatrolQueueKey, () => string> = {
   guides: () => _js('Guide'),
   wikis: () => _js('Wiki'),
   areas: () => _js('Wiki Area'),
};

/**
 * The rows a view lists. A queue is handed over in the order the server
 * sorted it, and the merged view sorts on `sortDate`, the key each queue
 * used, so a document sits in the same place relative to its own kind as
 * it does on its own tab.
 */
function viewRows(queues: PatrolQueues, view: PatrolViewKey): PatrolRow[] {
   if (view !== 'all') {
      return queues[view].map(item => ({ ...item, queue: view }));
   }
   return QUEUE_KEYS.flatMap(queue => queues[queue].map(item => ({ ...item, queue }))).sort(
      (left, right) => Date.parse(left.sortDate) - Date.parse(right.sortDate)
   );
}

function viewCount(queues: PatrolQueues | undefined, view: PatrolViewKey): number | undefined {
   if (!queues) {
      return undefined;
   }
   return view === 'all'
      ? QUEUE_KEYS.reduce((total, queue) => total + queues[queue].length, 0)
      : queues[view].length;
}

export function PatrolDashboard(props: PatrolProps) {
   const filterKeys = props.filters.map(filter => filter.key);
   const [filter, setFilter] = useLanguageFilter(filterKeys, props.initialData.filter);
   // useHashTab only ever returns a member of VIEW_KEYS or the fallback.
   // The merged view is that fallback: an empty hash is a fresh /Patrol,
   // and landing on everything at once is what saves the first click.
   const [activeTab, setActiveTab] = useHashTab(VIEW_KEYS, 'all');
   const activeView = activeTab as PatrolViewKey;
   const [page, setPage] = usePageParam();
   const queuePanelRef = useRef<HTMLElement | null>(null);
   const query = usePatrolQueues(filter, props.initialData);
   const queues = query.data;

   // A page number only means something within one queue and filter; switching
   // either starts back at page 1 (without a history entry — the tab/filter change
   // already made one). The previous-value ref keeps the reset off the initial
   // mount so ?page= deep links survive. Back and Forward must skip it too: the
   // restored entry carries its own page, and popstate fires before the hash/search
   // state updates, so the snapshot ref marks the arriving view as history-driven.
   const previousView = useRef({ queue: activeView, filter });
   const restoredView = useRef<{ queue: string; filter: string } | null>(null);
   useEffect(() => {
      const onPopState = () => {
         const hash = window.location.hash.slice(1);
         restoredView.current = {
            queue: VIEW_KEYS.includes(hash as PatrolViewKey) ? hash : 'all',
            filter:
               new URLSearchParams(window.location.search).get(LANGUAGE_FILTER_PARAM) ||
               props.initialData.filter,
         };
      };
      window.addEventListener('popstate', onPopState);
      return () => window.removeEventListener('popstate', onPopState);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);
   useEffect(() => {
      const changed =
         previousView.current.queue !== activeView || previousView.current.filter !== filter;
      previousView.current = { queue: activeView, filter };
      const restored = restoredView.current;
      restoredView.current = null;
      const isHistoryRestore =
         restored !== null && restored.queue === activeView && restored.filter === filter;
      if (changed && !isHistoryRestore) {
         setPage(1, { replace: true });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [activeView, filter]);

   const changePage = (nextPage: number) => {
      setPage(nextPage);
      queuePanelRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
   };

   // Sorting the merged view is the one step here that isn't linear in the
   // rows it draws, so it runs per payload rather than per render.
   const items = useMemo(
      () => (queues ? viewRows(queues, activeView) : undefined),
      [queues, activeView]
   );
   const pageCount = items ? Math.max(1, Math.ceil(items.length / ITEMS_PER_PAGE)) : 1;
   // A ?page= past the end (a deep link into a backlog since worked off) shows the
   // last page that exists rather than an empty card.
   const currentPage = Math.min(page, pageCount);

   return (
      <div className="mx-auto w-full max-w-5xl px-4 pb-10 md:px-8">
         {/* The community nav above renders tabs-only for this page; the H1,
             language filter, and help link all live here so the whole header is
             one component. */}
         <header className="pt-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
               <h1 className="text-[30px] font-medium text-gray-950">{_js('Patrol')}</h1>
               <label className="flex items-center gap-2 text-sm text-gray-700">
                  {_js('Show')}
                  <Select value={filter} onValueChange={setFilter}>
                     <SelectTrigger className="h-9 w-56 bg-white">
                        <SelectValue />
                     </SelectTrigger>
                     <SelectContent>
                        {props.filters.map(option => (
                           <SelectItem key={option.key} value={option.key}>
                              {option.label}
                           </SelectItem>
                        ))}
                     </SelectContent>
                  </Select>
               </label>
            </div>
            <p className="mt-2 max-w-2xl text-sm text-gray-700">
               {_js(
                  'Community edits that are ready for review. You can accept or deny edits to any page you have enough reputation points for.'
               )}
            </p>
         </header>

         <QueueTabs
            queues={queues}
            hasFailed={query.isError && !queues}
            activeView={activeView}
            onSelect={setActiveTab}
         />

         <section
            ref={queuePanelRef}
            id="patrol-panel"
            role="tabpanel"
            aria-labelledby={`patrol-tab-${activeView}`}
            className="mt-4 scroll-mt-4"
         >
            <div className={cn(QUEUE_CARD_CLASS, query.isPlaceholderData && 'opacity-60')}>
               {items ? (
                  items.length === 0 ? (
                     <EmptyQueue view={activeView} />
                  ) : (
                     <>
                        <QueueColumnHeaders />
                        <ul className="my-0 list-none divide-y divide-gray-200 pl-0">
                           {items
                              .slice(
                                 (currentPage - 1) * ITEMS_PER_PAGE,
                                 currentPage * ITEMS_PER_PAGE
                              )
                              .map(item => (
                                 <QueueRow
                                    // The url, not the docid: a queue holds
                                    // one row per document per language, so
                                    // a guide with both a pending edit and a
                                    // pending translation is two rows that
                                    // share a docid. The url carries the
                                    // langid, which is what tells them apart.
                                    // The queue joins it because the merged
                                    // view draws all three at once.
                                    key={`${item.queue}-${item.url}`}
                                    item={item}
                                    // Only the merged view has rows of more
                                    // than one kind to tell apart.
                                    showType={activeView === 'all'}
                                    href={entryHref(item.url, filter, activeView, currentPage)}
                                 />
                              ))}
                        </ul>
                     </>
                  )
               ) : query.isError ? (
                  <MessageCard
                     icon="triangle-exclamation"
                     tone="failure"
                     title={_js('The patrol queues didn’t load')}
                     text={_js('Something went wrong on our side. Reload to try again.')}
                  />
               ) : (
                  <QueueSkeleton hasLanguagePair={activeView === 'guides' && filter !== 'local'} />
               )}
            </div>
            {items && (
               <QueuePagination
                  page={currentPage}
                  pageCount={pageCount}
                  filter={filter}
                  view={activeView}
                  onPageChange={changePage}
               />
            )}
         </section>

         {/* The Translate card is only here on a site that runs the
             machine-translation queue, and one card in a two-column grid
             reads as a second one failing to load. */}
         <div className={cn('mt-8 grid grid-cols-1 gap-4', props.translateUrl && 'sm:grid-cols-2')}>
            {props.translateUrl && (
               <FooterCard
                  href={props.translateUrl}
                  icon="language"
                  title={_js('Review machine translations')}
                  description={_js(
                     'Machine translations publish right away and never wait here. They get reviewed on the Translate dashboard instead.'
                  )}
               />
            )}
            <FooterCard
               href={props.permissionsUrl}
               icon="shield-check"
               title={_js('How patrolling works')}
               description={_js(
                  'Reputation decides which pages you can patrol. See how the thresholds work and how to raise yours.'
               )}
            />
         </div>
      </div>
   );
}

export function QueueTabs({
   queues,
   hasFailed,
   activeView,
   onSelect,
}: {
   queues: PatrolQueues | undefined;
   hasFailed: boolean;
   activeView: PatrolViewKey;
   onSelect: (key: PatrolViewKey) => void;
}) {
   const tabButtonsRef = useRef<Array<HTMLAnchorElement | null>>([]);
   // Manual activation: arrows move focus only; Enter (the link's own behaviour) or
   // Space commits. Selecting resets to page 1, so arrowing across the strip to
   // reach the third queue must not trigger that on the way through.
   const [focusedQueue, setFocusedQueue] = useState<PatrolViewKey | null>(null);

   const select = (key: PatrolViewKey) => {
      // Reselecting the queue you're on would push a duplicate history entry and
      // yank someone on page 4 back to page 1.
      if (key !== activeView) {
         onSelect(key);
      }
   };

   const onTabKeyDown = (event: ReactKeyboardEvent<HTMLAnchorElement>, index: number) => {
      if (event.key === ' ') {
         // Space activates a button but not a link; a tab expects it.
         event.preventDefault();
         select(VIEW_CARDS[index].key);
         return;
      }
      const nextIndex = {
         ArrowRight: index + 1,
         ArrowDown: index + 1,
         ArrowLeft: index - 1,
         ArrowUp: index - 1,
         Home: 0,
         End: VIEW_CARDS.length - 1,
      }[event.key];
      if (nextIndex !== undefined) {
         event.preventDefault();
         tabButtonsRef.current[(nextIndex + VIEW_CARDS.length) % VIEW_CARDS.length]?.focus();
      }
   };

   return (
      <div
         role="tablist"
         aria-label={_js('Patrol queues')}
         onBlur={event => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
               setFocusedQueue(null);
            }
         }}
         // scroll-px keeps the snap positions honest: snapping aligns to
         // scroll-padding, not the box padding, so without it the first pill snaps
         // flush to the screen edge.
         className="mt-6 flex gap-2 max-md:-mx-4 max-md:snap-x max-md:overflow-x-auto max-md:px-4 max-md:scroll-px-4 max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden md:grid md:grid-cols-4"
      >
         {VIEW_CARDS.map((card, index) => {
            const isActive = card.key === activeView;
            const count = viewCount(queues, card.key);
            return (
               <a
                  key={card.key}
                  ref={element => {
                     tabButtonsRef.current[index] = element;
                  }}
                  href={`#${card.key}`}
                  role="tab"
                  id={`patrol-tab-${card.key}`}
                  aria-selected={isActive}
                  aria-controls="patrol-panel"
                  // Roving tabindex: follows the focused tab, falls back to the
                  // active one when focus leaves the strip.
                  tabIndex={(focusedQueue ?? activeView) === card.key ? 0 : -1}
                  onFocus={() => setFocusedQueue(card.key)}
                  onKeyDown={event => onTabKeyDown(event, index)}
                  className={cn(
                     'flex min-w-0 flex-col gap-0.5 rounded-2xl border bg-white px-3 py-2 no-underline transition-colors hover:no-underline',
                     'max-md:w-64 max-md:shrink-0 max-md:snap-start',
                     'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                     // The inset shadow doubles the 1px border into a 2px stroke
                     // without shifting layout or fighting the focus ring.
                     isActive
                        ? 'border-brand-500 text-brand-500 shadow-[inset_0_0_0_1px_var(--color-brand-500)]'
                        : 'border-gray-200 text-gray-900 hover:border-gray-300'
                  )}
               >
                  <span className="flex items-center gap-1.5 whitespace-nowrap text-sm font-medium">
                     <FaIcon name={card.icon} className="size-3.5" aria-hidden />
                     {/* The row can't wrap, so a long translated title gives here
                         rather than pushing the count out. */}
                     <span className="min-w-0 truncate">{card.title()}</span>
                     {count === undefined ? (
                        hasFailed ? (
                           <Badge
                              className={cn(
                                 'badge-sm ml-auto',
                                 isActive ? 'badge-brand' : 'badge-gray'
                              )}
                           >
                              —
                           </Badge>
                        ) : (
                           <Skeleton className="ml-auto h-5 w-9 rounded-full" />
                        )
                     ) : (
                        <Badge
                           className={cn(
                              'badge-sm ml-auto',
                              isActive ? 'badge-brand' : 'badge-gray'
                           )}
                        >
                           {localizeAndFormat(count)}
                        </Badge>
                     )}
                  </span>
                  <span className="pl-5 text-xs text-gray-500">{card.description()}</span>
               </a>
            );
         })}
      </div>
   );
}

function EmptyQueue({ view }: { view: PatrolViewKey }) {
   const copy = {
      all: {
         title: _js('Nothing is waiting for review'),
         text: _js('Every pending edit has been reviewed, in all three queues.'),
      },
      guides: {
         title: _js('No guide edits are waiting'),
         text: _js('Every pending edit to a repair guide has been reviewed.'),
      },
      wikis: {
         title: _js('No page edits are waiting'),
         text: _js('Every pending edit to a device or topic page has been reviewed.'),
      },
      areas: {
         title: _js('No hierarchy changes are waiting'),
         text: _js('Every page that moved in the device hierarchy has been reviewed.'),
      },
   }[view];
   return <MessageCard icon="circle-check" tone="done" title={copy.title} text={copy.text} />;
}

function QueueColumnHeaders() {
   return (
      <div className={cn('hidden border-b border-border bg-gray-100 px-4 py-2', QUEUE_GRID_CLASS)}>
         {/* No heading for the thumbnail column — the image is the label — but the
             track still needs its cell. */}
         <span />
         <span className={COLUMN_LABEL_ON_FILL_CLASS}>{_js('Page')}</span>
         <span className={cn('text-right', COLUMN_LABEL_ON_FILL_CLASS)}>{_js('Revisions')}</span>
      </div>
   );
}

/** Entry links name the view they left (the entry page hands it back on
 *  its "Back to Patrol"), so settling an edit doesn't reset the queue. */
function entryHref(
   url: string,
   filter: PatrolLanguageFilter,
   view: PatrolViewKey,
   page: number
): string {
   const params = new URLSearchParams();
   params.set(LANGUAGE_FILTER_PARAM, filter);
   params.set('queue', view);
   if (page > 1) {
      params.set('page', String(page));
   }
   return `${url}?${params.toString()}`;
}

function QueueRow({ item, href, showType }: { item: PatrolRow; href: string; showType: boolean }) {
   return (
      <li
         data-testid="patrol-item"
         data-docid={item.docid}
         // The count is printed twice (here and below); which copy is visible
         // depends on the breakpoint.
         data-revision-count={item.revisionCount}
         className={cn(
            'relative flex items-start gap-3 px-4 py-3 transition-colors hover:bg-gray-50',
            QUEUE_GRID_CLASS
         )}
      >
         {item.thumbnail ? (
            <img
               src={item.thumbnail}
               alt=""
               width="48"
               height="36"
               loading="lazy"
               className="my-0 aspect-[4/3] w-12 shrink-0 rounded-md border border-gray-200 bg-gray-50 object-cover"
            />
         ) : (
            <span className="flex aspect-[4/3] w-12 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-gray-400">
               <FaIcon name="image" className="size-4 fill-current" aria-hidden />
            </span>
         )}
         <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2">
               <a
                  href={href}
                  data-testid="patrol-item-link"
                  // break-words: min-w-0 alone won't wrap an unspaced title, which a
                  // German compound often is.
                  className="rounded-sm font-medium break-words text-gray-900 after:absolute after:inset-0 hover:text-brand-500 hover:no-underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
               >
                  {item.title}
               </a>
               {showType && (
                  <Badge data-testid="patrol-item-type" className="badge-gray badge-sm shrink-0">
                     {QUEUE_LABELS[item.queue]()}
                  </Badge>
               )}
            </div>
            {item.translation && (
               <span className="mt-0.5 flex text-xs text-gray-500">
                  <LanguagePairChips pair={item.translation} />
               </span>
            )}
            <div className="text-xs text-gray-500">
               <EditedBy item={item} />
               <span className="md:hidden">
                  {' · '}
                  {revisionsLabel(item.revisionCount)}
               </span>
            </div>
         </div>
         {/* Column alignment carries this cell's meaning to the eye but not to a
             screen reader, so it names itself as the stacked rows do. */}
         <div className="text-right text-sm tabular-nums text-gray-600 max-md:hidden">
            <span aria-hidden="true">{localizeAndFormat(item.revisionCount)}</span>
            <span className="sr-only">{revisionsLabel(item.revisionCount)}</span>
         </div>
      </li>
   );
}

function EditedBy({ item }: { item: PatrolRow }) {
   return (
      <>
         {/* No "Last edited" label: splitting the sentence would leave translators
             a fragment, and the date is the only one of these three pieces that
             needs no naming. */}
         <time dateTime={item.editedAt}>{item.editedLabel}</time>
         {' · '}
         {/* Positioned above the title link's row-wide overlay, which would
             otherwise swallow the click. */}
         <a
            href={item.userUrl}
            className="relative z-[1] rounded-sm text-gray-600 hover:text-brand-500 hover:no-underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
         >
            {item.username}
         </a>
         {' · '}
         {/* Translators: %1 -> the editor's reputation; */}
         {_js('%1 reputation', localizeAndFormat(item.userReputation))}
      </>
   );
}

// Mirrors QueueRow's anatomy — thumbnail, title, edit line, revision count — on the
// same grid, so the real rows land without a reflow. Only guide rows draw the
// language pair, so the merged view never gets the extra line, and only the
// translations language filter guarantees every guide row is a translation —
// under that filter's "all" the line is a guess.
function QueueSkeleton({ hasLanguagePair }: { hasLanguagePair: boolean }) {
   return (
      <div className="divide-y divide-gray-200">
         {Array.from({ length: 8 }, (_, index) => (
            <div key={index} className={cn('flex items-start gap-3 px-4 py-3', QUEUE_GRID_CLASS)}>
               <Skeleton className="aspect-[4/3] w-12 rounded-md" />
               <div className="w-full space-y-2 md:w-auto">
                  <Skeleton className="h-4 w-2/5 max-md:w-44" />
                  {hasLanguagePair && <Skeleton className="h-4 w-16" />}
                  <Skeleton className="h-3 w-1/4 max-md:w-56" />
               </div>
               <Skeleton className="h-3 w-32 max-md:hidden" />
               <Skeleton className="h-4 w-8 max-md:hidden md:justify-self-end" />
            </div>
         ))}
      </div>
   );
}

// The whole backlog for a filter arrives in one response, so paging here is a
// slice of what's already fetched.
function QueuePagination({
   page,
   pageCount,
   filter,
   view,
   onPageChange,
}: {
   page: number;
   pageCount: number;
   filter: PatrolLanguageFilter;
   view: PatrolViewKey;
   onPageChange: (page: number) => void;
}) {
   // The href carries the language filter and the queue too: a fresh load's URL
   // carries neither, so a copied link would otherwise land on the default filter
   // and the first queue instead of what's on screen.
   const pageHref = (target: number) => {
      const url = new URL(window.location.href);
      url.searchParams.set(LANGUAGE_FILTER_PARAM, filter);
      url.searchParams.set('page', String(target));
      url.hash = view;
      return url.pathname + url.search + url.hash;
   };
   return (
      <DashboardPagination
         page={page}
         pageCount={pageCount}
         pageHref={pageHref}
         onPageChange={onPageChange}
      />
   );
}

function revisionsLabel(count: number): string {
   /* Translators: %1 -> number of revisions; */
   return ___p(count, '%1 revision', '%1 revisions', localizeAndFormat(count));
}

// Matches the old dashboard's param name, so links people saved keep the filter
// they picked.
const LANGUAGE_FILTER_PARAM = 'langFilter';

function useLanguageFilter(
   validFilters: PatrolLanguageFilter[],
   fallback: PatrolLanguageFilter
): [PatrolLanguageFilter, (filter: PatrolLanguageFilter) => void] {
   const parse = (raw: string | null): PatrolLanguageFilter =>
      raw !== null && validFilters.includes(raw as PatrolLanguageFilter)
         ? (raw as PatrolLanguageFilter)
         : fallback;
   // The filter is part of the SQL query, so changing it is the one control here
   // that costs a round trip.
   return useUrlParam<PatrolLanguageFilter>(
      LANGUAGE_FILTER_PARAM,
      parse(new URLSearchParams(window.location.search).get(LANGUAGE_FILTER_PARAM)),
      parse,
      filter => filter
   );
}
